"use client";

import { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/utils';
import { CreditCard } from 'lucide-react';
import { getCompanySettings } from '@/lib/api';
import { getApiBaseUrl } from '@/lib/environment';

interface StripeCheckoutInlineProps {
  companyId: string;
  amount: number;
  currency?: string;
  description?: string;
  orderId?: string;
  invoiceId?: string;
  customerId?: string;
  onSuccess: (transactionId: string, last4?: string) => void;
  onError: (message: string) => void;
  savePaymentMethod?: boolean;
  customerEmail?: string;
  customerName?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  platformFeeEnabled?: boolean;
  platformFeePercentage?: number;
  platformFeeAmount?: number;
  salesTax?: number;
  publishableKey?: string; // optional override
  // Donation-specific context (optional). When provided and invoiceId looks
  // like a donation temp id, we will use the donation API which records
  // both transaction and donation consistently across gateways.
  donationBaseAmount?: number;
  donationDynamicFields?: Record<string, string>;
}

export function StripeCheckoutInline(props: StripeCheckoutInlineProps) {
  const [pk, setPk] = useState<string>('');
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        // Priority: explicit prop -> tenant settings (no environment fallback)
        const propPk = props.publishableKey;
        if (propPk) {
          setPk(String(propPk));
          setReady(true);
          setError('');
          return;
        }

        // Always use company-configured publishable key
        // Super admin's configured keys are the single source of truth
        const settings = await getCompanySettings(props.companyId);
        const stripeCfg = settings.paymentGateways?.providers?.stripe;
        if (stripeCfg?.enabled === false) {
          throw new Error('Stripe is disabled for this tenant');
        }
        const publishable = stripeCfg?.credentials?.publishableKey;
        if (!publishable) {
          throw new Error('Stripe publishable key not configured by administrator');
        }
        setPk(String(publishable));
        setReady(true);
        setError('');
      } catch (e: any) {
        console.error('[Stripe] Initialization failed:', e?.message || e);
        setReady(false);
        setError('Stripe is not configured for this tenant.');
      }
    })();
  }, [props.companyId, props.publishableKey]);

  const stripePromise = useMemo(() => (pk ? loadStripe(pk) : null), [pk]);
  if (!ready || !stripePromise) {
    return error ? <div className="text-sm text-red-600">{error}</div> : null;
  }

  return (
    <Elements stripe={stripePromise}>
      <StripeCardFormInner {...props} />
    </Elements>
  );
}

function StripeCardFormInner(props: StripeCheckoutInlineProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      const card = elements.getElement(CardElement);
      if (!card) throw new Error('Card element not found');
      const pmResult = await stripe.createPaymentMethod({ 
        type: 'card', 
        card,
        billing_details: {
          name: (props.customerName || '').trim() || undefined,
          email: (props.customerEmail || '').trim() || undefined,
          address: {
            line1: (props.address || undefined) as any,
            city: (props.city || undefined) as any,
            state: (props.state || undefined) as any,
            postal_code: (props.zip || undefined) as any,
            country: (props.country || undefined) as any,
          }
        }
      });
      if (pmResult.error || !pmResult.paymentMethod) {
        throw new Error(pmResult.error?.message || 'Failed to tokenize card');
      }
      const tokenId = pmResult.paymentMethod.id;
      // Detect donation context by temporary invoice id pattern
      const isDonation = (props.invoiceId || '').startsWith('donation-');

      // Charge via appropriate API (donation-aware when in donation flow)
      const endpoint = isDonation ? '/api/payments/donation' : '/api/payments/charge';

      // Build body for endpoint
      const body = isDonation ? {
        companyId: props.companyId,
        amount: props.amount, // total charge amount (may include fees/tax)
        currency: props.currency || 'usd',
        tokenId,
        customerId: props.customerId,
        savePaymentMethod: !!props.savePaymentMethod,
        platformFeeEnabled: props.platformFeeEnabled,
        platformFeePercentage: props.platformFeePercentage,
        platformFeeAmount: props.platformFeeAmount,
        salesTax: props.salesTax,
        // donationData contains the base donor info and dynamic fields
        donationData: {
          companyId: props.companyId,
          amount: props.donationBaseAmount ?? props.amount,
          firstName: (props.customerName || '').split(' ')[0] || '',
          lastName: (props.customerName || '').split(' ').slice(1).join(' ') || '',
          email: props.customerEmail || '',
          address: props.address || '',
          city: props.city || '',
          state: props.state || '',
          zip: props.zip || '',
          description: props.description?.trim() || '',
          dynamicFields: props.donationDynamicFields || {},
        }
      } : {
        companyId: props.companyId,
        amount: props.amount,
        currency: props.currency || 'usd',
        source: 'new',
        tokenId,
        description: props.description,
        orderId: props.orderId,
        invoiceId: props.invoiceId,
        customerId: props.customerId,
        customerName: props.customerName,
        customerEmail: props.customerEmail,
        address: props.address,
        city: props.city,
        state: props.state,
        zip: props.zip,
        country: props.country,
        platformFeeEnabled: props.platformFeeEnabled,
        platformFeePercentage: props.platformFeePercentage,
        platformFeeAmount: props.platformFeeAmount,
        salesTax: props.salesTax,
        kind: 'card',
        savePaymentMethod: !!props.savePaymentMethod && !!props.customerId,
      };

      const apiBaseUrl = getApiBaseUrl();
      const url = `${apiBaseUrl}${endpoint}`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data?.error || 'Charge failed');
      }
      // Do not separately save the method here when using PaymentIntents; the server
      // will attach the payment method to a Stripe customer when savePaymentMethod is true
      props.onSuccess(data.transactionId, data.last4);
    } catch (e: any) {
      props.onError(e?.message || 'Stripe payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="border rounded p-3">
        <CardElement options={{ hidePostalCode: true }} />
      </div>
      <Button
        onClick={handlePay}
        disabled={submitting}
        className="w-full h-12 text-base font-medium rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors"
      >
        {submitting ? (
          'Processing…'
        ) : (
          <span className="inline-flex items-center">
            <CreditCard className="mr-2 h-5 w-5" />
            {`Pay ${formatCurrency(props.amount)}`}
          </span>
        )}
      </Button>
    </div>
  );
}
